export type RuntimeConfig = {
  ssoUrl: string;
  webHomeUrl: string;
  webHomeUrls: Record<string, string>;
};

type RuntimeConfigPayload = {
  ssoUrl: string;
  webHomeUrl?: string;
  webHomeUrls?: Record<string, string>;
};

const DEFAULT_RUNTIME_CONFIG: RuntimeConfig = {
  ssoUrl: 'https://sso-v7.over-c.net',
  webHomeUrl: 'https://v7-home.over-c.net/#/',
  webHomeUrls: {},
};

let runtimeConfig = DEFAULT_RUNTIME_CONFIG;

function normalizeUrl(url: string): string {
  return url.replace(/\/+$/, '');
}

function getDefaultWebHomeUrl(ssoUrl: string): string {
  return normalizeUrl(ssoUrl) === 'https://prod-sso.over-c.net'
    ? 'https://over-c.net'
    : DEFAULT_RUNTIME_CONFIG.webHomeUrl;
}

function isWebHomeUrls(value: unknown): value is Record<string, string> {
  return (
    typeof value === 'object' &&
    value !== null &&
    Object.values(value).every(
      (webHomeUrl) => typeof webHomeUrl === 'string' && webHomeUrl.length > 0,
    )
  );
}

function isRuntimeConfigPayload(value: unknown): value is RuntimeConfigPayload {
  const config = value as RuntimeConfigPayload;

  return (
    typeof value === 'object' &&
    value !== null &&
    typeof config.ssoUrl === 'string' &&
    config.ssoUrl.length > 0 &&
    (
      config.webHomeUrl === undefined ||
      (
        typeof config.webHomeUrl === 'string' &&
        config.webHomeUrl.length > 0
      )
    ) &&
    (config.webHomeUrls === undefined || isWebHomeUrls(config.webHomeUrls))
  );
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  try {
    const response = await fetch('/config.json', { cache: 'no-store' });

    if (!response.ok) return runtimeConfig;

    const config = await response.json();

    if (isRuntimeConfigPayload(config)) {
      runtimeConfig = {
        ssoUrl: normalizeUrl(config.ssoUrl),
        webHomeUrl: config.webHomeUrl || getDefaultWebHomeUrl(config.ssoUrl),
        webHomeUrls: config.webHomeUrls || {},
      };
    }
  } catch {
    // Keep the baked-in default if runtime config cannot be loaded.
    runtimeConfig = DEFAULT_RUNTIME_CONFIG;
  }

  return runtimeConfig;
}

export function getRuntimeConfig(): RuntimeConfig {
  return runtimeConfig;
}

export function getSsoUrl(path: string): string {
  return `${runtimeConfig.ssoUrl}${path.startsWith('/') ? path : `/${path}`}`;
}

export function getWebHomeUrl(): string {
  const host = window.location.hostname.toLowerCase();
  const hostWebHomeUrl = runtimeConfig.webHomeUrls[host];

  if (hostWebHomeUrl) return hostWebHomeUrl;

  return runtimeConfig.webHomeUrl;
}
